import { useEffect, useRef, useState } from 'react';
import { Product, ProductType } from '../../../clients/quipli-service/product/product';
import {
  GeneralLedgerAccount,
  GeneralLedgerAccountType,
  ItemGeneralLedgerTransactionType,
} from '../../../clients/quipli-service/accounting/accounting';
import { useAccountingApiV1 } from '../../../clients/quipli-service/accounting/accounting-api-v1';
import { usePurchaseOrderApiV1 } from '../../../clients/quipli-service/purchase-order/purchase-order-api-v1';
import { useProductApiV1 } from '../../../clients/quipli-service/product/product-api-v1';
import { CollapsibleRef } from '../../../components/collapsible/collapsible';
import { v4 as uuid } from 'uuid';
import { toast } from '../../../util/toasts';
import { PurchaseOrderProductLineItem } from '../../../clients/quipli-service/purchase-order/purchase-order';
import { useFormValidationWithTouch } from '../../../hooks/use-form-validation-with-touch';
import z from 'zod';

/**
 * Determines the default general ledger account based on available accounts and item configuration
 * @param accounts - Available general ledger accounts
 * @param generalLedgerAccountId - The ID of the preferred account from item configuration
 * @returns The selected account or undefined
 */
export const determineDefaultAccount = (
  accounts: GeneralLedgerAccount[],
  generalLedgerAccountId: string | undefined,
): GeneralLedgerAccount | undefined => {
  if (!accounts.length) {
    return undefined;
  }

  if (!generalLedgerAccountId) {
    return accounts[0];
  }

  const existingAccount = accounts.find((a) => a.id === generalLedgerAccountId);

  return existingAccount || accounts[0];
};

interface UsePurchaseOrderItemFormProps {
  onAddItem: (item: PurchaseOrderProductLineItem) => void;
  openOnStart?: boolean;
}

const purchaseOrderItemFormSchema = z
  .object({
    price: z.number({ message: 'Price is required' }).gt(0, 'Price must be greater than 0'),
    qty: z.number({ message: 'Quantity is required' }).gt(0, 'Quantity must be greater than 0'),
    name: z.string().optional(),
    productType: z.string(),
  })
  .refine(
    (data) => {
      // If productType is Custom, name is required
      if (data.productType === ProductType.Custom) {
        return data.name && data.name.trim().length > 0;
      }
      return true;
    },
    {
      message: 'Name is required',
      path: ['name'],
    },
  );

export const usePurchaseOrderItemForm = ({ onAddItem, openOnStart = true }: UsePurchaseOrderItemFormProps) => {
  const { getCustomProduct } = useProductApiV1();
  const { defaultPriceForProduct } = usePurchaseOrderApiV1();
  const { listAccounts, getItemGeneralLedgerAccount } = useAccountingApiV1();

  // States
  const [productType, setProductType] = useState<ProductType>(ProductType.Product);
  const [customProduct, setCustomProduct] = useState<Product>();
  const [product, setProduct] = useState<Product | undefined>();
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [price, setPrice] = useState<number>(0);
  const [qty, setQty] = useState<number>(1);
  const [accounts, setAccounts] = useState<GeneralLedgerAccount[]>([]);
  const [generalLedgerAccount, setGeneralLedgerAccount] = useState<GeneralLedgerAccount | undefined>();
  const [defaultGeneralLedgerAccount, setDefaultGeneralLedgerAccount] = useState<GeneralLedgerAccount | undefined>();

  // Validation with touch tracking
  const { isValid, errors, markFieldAsTouched, clearTouchedFields } = useFormValidationWithTouch(
    purchaseOrderItemFormSchema,
    {
      price,
      qty,
      name,
      productType,
    },
  );

  const isDisabled = !isValid || (productType === ProductType.Custom ? !customProduct : !product);

  // Collapsible ref
  const collapsibleRef = useRef<CollapsibleRef>(null);

  const toggleCollapse = (open: boolean) => {
    collapsibleRef.current?.toggle(open);
  };

  // Collapse the form
  const collapse = () => {
    toggleCollapse(false);
  };

  // Clear the form
  const clear = () => {
    setProduct(undefined);
    setName('');
    setDescription('');
    setPrice(0);
    setQty(1);
    setGeneralLedgerAccount(defaultGeneralLedgerAccount);
    clearTouchedFields();
  };

  // Submit the form
  const submit = () => {
    if (isDisabled || !price) return;

    const commonFields = {
      id: uuid(),
      quantity: qty,
      unitPrice: price,
    };

    if (productType === ProductType.Custom) {
      onAddItem({
        name,
        description,
        generalLedgerAccountId: generalLedgerAccount?.id,
        product: customProduct!,
        ...commonFields,
      });
    } else {
      onAddItem({ ...commonFields, product: product! });
    }

    clear();
  };

  // Collapse and clear the form
  const collapseAndClear = () => {
    toggleCollapse(false);
    clear();
  };

  // Change the product type
  const changeProductType = (type: ProductType) => {
    clear();
    setProductType(type);
  };

  // Custom setters to use markFieldAsTouched
  const setPriceWithTouch = (price: number) => {
    setPrice(Number.isNaN(price) || price == null ? 0 : price);
    markFieldAsTouched('price');
  };
  const setQtyWithTouch = (qty: number) => {
    setQty(qty);
    markFieldAsTouched('qty');
  };
  const setNameWithTouch = (name: string) => {
    setName(name);
    markFieldAsTouched('name');
  };

  // Initialize the form
  useEffect(() => {
    openOnStart && toggleCollapse(true);
    listAccounts(false, GeneralLedgerAccountType.Expense).then((accountsToUse) => {
      setAccounts(accountsToUse);
      getItemGeneralLedgerAccount(undefined, undefined, ItemGeneralLedgerTransactionType.Expense).then((item) => {
        const defaultAccount = determineDefaultAccount(accountsToUse, item?.generalLedgerAccountId);
        setGeneralLedgerAccount(defaultAccount);
        setDefaultGeneralLedgerAccount(defaultAccount);
      });
    });
    getCustomProduct()
      .then(setCustomProduct)
      .catch((e) => {
        console.error(e);
        toast('Failed to fetch custom product', 'error');
      });
  }, []);

  // Set default price for the product
  useEffect(() => {
    if (!product) return;

    defaultPriceForProduct(product.id).then((dp) => {
      if (dp) {
        setPrice(dp);
      } else if (product?.purchasePrice) {
        setPrice(product.purchasePrice);
      }
    });
  }, [product]);

  return {
    // Ref
    collapsibleRef,

    // Helpers
    isDisabled,

    // States
    productType,
    price,
    qty,
    product,
    name,
    description,
    generalLedgerAccount,
    accounts,

    // Validation
    errors,

    // Setters
    setPrice: setPriceWithTouch,
    setQty: setQtyWithTouch,
    setName: setNameWithTouch,
    setDescription,
    setGeneralLedgerAccount,
    setProduct,

    // Callbacks
    collapse,
    collapseAndClear,
    submit,
    changeProductType,
  };
};
